import { useEffect, useRef, useCallback, useState } from "react";
import { useTranslations } from "use-intl";

import {
  CloseIcon,
  CollapseIcon,
  ExpandFullscreenIcon,
  OpenExternalIcon,
  SkipNextIcon,
  SkipPreviousIcon,
  VideoDisabledIcon,
} from "@/components/appIcons";
import { Link } from "@/components/link";
import { useVideoPlayer } from "@/contexts/video-player-context";

/** YouTube IFrame Player API state constants. */
const YT_ENDED = 0;

/** YouTube IFrame Player API error codes that mean "can't embed this video". */
const YT_UNPLAYABLE_ERRORS = new Set([100, 101, 150]);
const YOUTUBE_API_SCRIPT_ID = "youtube-iframe-api";

function loadYouTubeApi() {
  if (window.YT?.Player || document.getElementById(YOUTUBE_API_SCRIPT_ID)) return;
  const script = document.createElement("script");
  script.id = YOUTUBE_API_SCRIPT_ID;
  script.src = "https://www.youtube.com/iframe_api";
  script.async = true;
  document.head.append(script);
}

type PlayerSize = "small" | "large";

const SIZE_CLASSES: Record<PlayerSize, string> = {
  small: "w-[calc(100vw-2rem)] sm:w-[400px]",
  large: "w-[calc(100vw-2rem)] sm:w-[640px]",
};

export function FloatingVideoPlayer() {
  const t = useTranslations("shared.videoPlayer");
  const { state, currentTrack, hasPrevious, hasNext, previous, next, close } = useVideoPlayer();
  const playerRef = useRef<YT.Player | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<PlayerSize>("small");
  const SizeIcon = size === "small" ? ExpandFullscreenIcon : CollapseIcon;
  const [isUnplayable, setIsUnplayable] = useState(false);

  const toggleSize = useCallback(() => {
    setSize((prev) => (prev === "small" ? "large" : "small"));
  }, []);

  const onVideoEnd = useCallback(() => {
    if (hasNext) next();
  }, [hasNext, next]);

  // Keep onVideoEnd ref in sync so the player callback always calls the latest version
  const onVideoEndRef = useRef(onVideoEnd);
  onVideoEndRef.current = onVideoEnd;

  // Create player once, reuse it for subsequent track changes via loadVideoById.
  // Destroying and recreating the player causes browsers to block autoplay in
  // background tabs, which prevents auto-advance to the next track.
  useEffect(() => {
    // Reset error whenever the track changes — the next video may be playable.
    setIsUnplayable(false);

    // When closing the player, destroy the YouTube instance so the
    // next play() creates a fresh player with a valid container
    if (!currentTrack) {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      return;
    }

    if (!containerRef.current) return;

    // Reuse existing player — just load the new video
    if (playerRef.current) {
      playerRef.current.loadVideoById(currentTrack.youtubeId);
      return;
    }

    const createPlayer = () => {
      playerRef.current = new YT.Player(containerRef.current!, {
        videoId: currentTrack.youtubeId,
        playerVars: { autoplay: 1, rel: 0 },
        events: {
          onStateChange: (event: YT.OnStateChangeEvent) => {
            if (event.data === YT_ENDED) onVideoEndRef.current();
          },
          onError: (event: YT.OnErrorEvent) => {
            if (YT_UNPLAYABLE_ERRORS.has(event.data)) setIsUnplayable(true);
          },
        },
      });
    };

    if (window.YT?.Player) {
      createPlayer();
    } else {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        prev?.();
        if (containerRef.current) createPlayer();
      };
      loadYouTubeApi();
    }
  }, [currentTrack?.youtubeId]);

  // Destroy player only on unmount
  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, []);

  if (!state || !currentTrack) return null;

  const sourceLabel =
    [state.artistName, state.source?.title].filter(Boolean).join(" — ") || currentTrack.trackTitle;

  return (
    <div
      className={`fixed bottom-16 right-4 xl:bottom-4 z-40 rounded-xl bg-background border border-border shadow-2xl overflow-hidden transition-[width] duration-200 ${SIZE_CLASSES[size]}`}
    >
      {/* YouTube player container */}
      <div className="relative w-full" style={{ aspectRatio: "16/9" }}>
        <div ref={containerRef} className="absolute inset-0 w-full h-full" />
        {isUnplayable && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/90 text-white p-4 text-center">
            <VideoDisabledIcon className="text-4xl opacity-70" />
            <p className="text-sm opacity-80">{t("unavailable")}</p>
            <a
              href={`https://www.youtube.com/watch?v=${currentTrack.youtubeId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-black text-xs font-medium hover:bg-white/90 transition-colors"
            >
              <OpenExternalIcon className="text-base" />
              {t("watchOnYoutube")}
            </a>
          </div>
        )}
      </div>
      {/* Info + controls bar */}
      <div className="flex items-center gap-2 px-3 py-2 border-t border-border">
        <div className="flex items-center gap-1">
          <button
            onClick={previous}
            disabled={!hasPrevious}
            className="relative after:absolute after:-inset-2 after:content-[''] p-1 rounded-full hover:bg-secondary transition-colors disabled:opacity-30 disabled:cursor-default"
            aria-label={t("previous")}
          >
            <SkipPreviousIcon className="text-lg" />
          </button>
          <button
            onClick={next}
            disabled={!hasNext}
            className="relative after:absolute after:-inset-2 after:content-[''] p-1 rounded-full hover:bg-secondary transition-colors disabled:opacity-30 disabled:cursor-default"
            aria-label={t("next")}
          >
            <SkipNextIcon className="text-lg" />
          </button>
        </div>

        <div className="flex-1 min-w-0">
          {state.source ? (
            <Link
              to={state.source.url}
              className="text-sm font-medium truncate block hover:text-primary transition-colors"
              title={sourceLabel}
            >
              {sourceLabel}
            </Link>
          ) : (
            <p className="text-sm font-medium truncate" title={sourceLabel}>
              {sourceLabel}
            </p>
          )}
          <p className="text-xs text-muted-foreground truncate" title={currentTrack.trackTitle}>
            {currentTrack.trackTitle}
            <span className="ml-1.5 opacity-70">
              ({t("trackOf", { current: state.currentIndex + 1, total: state.playlist.length })})
            </span>
          </p>
        </div>

        <button
          onClick={toggleSize}
          className="relative after:absolute after:-inset-2 after:content-[''] p-1 rounded-full hover:bg-secondary transition-colors hidden sm:block"
          aria-label={t(size === "small" ? "expand" : "collapse")}
        >
          <SizeIcon className="text-lg" />
        </button>

        <button
          onClick={close}
          className="relative after:absolute after:-inset-2 after:content-[''] p-1 rounded-full hover:bg-secondary transition-colors"
          aria-label={t("closePlayer")}
        >
          <CloseIcon className="text-lg" />
        </button>
      </div>
    </div>
  );
}
